// Section 4 phase 7 and Section 13: crime, detection and justice (stream M4a). In order:
//   1. drop expired deterrence effects and old raid losses;
//   2. apply last season's answered justice prompts (ask each case, E1-08);
//   3. roll each grown-up's crime (commit.ts), detect, open cases and judge them (cases.ts);
//   4. record the season's safety points and warn when settlement trust is below 40.
// The phase never pauses: justice questions are open prompts answered in the next Decision.
import { safetyPoints, settlementTrust } from '../../core/queries';
import type { Phase, PhaseContext } from '../../core/types';
import { resolveAnswered } from './cases';
import { rollCrimes } from './commit';
import { newRun, reportTrust } from './trust';

function tidy(ctx: PhaseContext): void {
  const d = ctx.draft;
  const turn = ctx.season.turn;
  if (d.deterrence.some((e) => e.untilTurn < turn))
    d.deterrence = d.deterrence.filter((e) => e.untilTurn >= turn);
  const keepAfter = turn - ctx.cfg.threats.safetyLookbackSeasons;
  if (d.safety.raidLosses.some((r) => r.turn <= keepAfter))
    d.safety.raidLosses = d.safety.raidLosses.filter((r) => r.turn > keepAfter);
}

export const crimePhase: Phase = {
  id: 'crime',
  reads: [
    'people',
    'households',
    'buildings',
    'stocks',
    'policies',
    'cases',
    'deterrence',
    'safety',
    'openPrompts',
    'promptAnswers',
    'norms',
    'currentReport',
    'history',
  ],
  writes: [
    'people',
    'households',
    'formerPeople',
    'buildings',
    'stocks',
    'cases',
    'deterrence',
    'safety',
    'openPrompts',
    'nextIds',
    'plantedPlots',
    'toolOverrides',
    'triageOverrides',
    'rationOverrides',
    'myHousehold',
    'currentReport',
  ],
  projection: 'skip',
  run(ctx) {
    const run = newRun();
    tidy(ctx);
    resolveAnswered(ctx, run);
    rollCrimes(ctx, run);
    const report = ctx.report.current;
    report.services.safetyPoints = safetyPoints(ctx.state);
    const trust = settlementTrust(ctx.state);
    if (trust < ctx.cfg.trust.crimeAndEmigrationBelow)
      ctx.report.add({ kind: 'warning', code: 'low-trust', amount: Math.round(trust) });
    reportTrust(ctx, run);
    return 'done';
  },
};
